<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import WhatsappCampaignsAPI from 'dashboard/api/whatsappCampaigns';
import WhatsAppTemplateBuilderModal from '../components/WhatsAppTemplateBuilderModal.vue';
const whatsappInboxes = useMapGetter('inboxes/getWhatsAppInboxes');

const selectedInboxId = ref('');
const templates = ref([]);
const loading = ref(false);
const syncing = ref(false);
const deletingId = ref(null);
const showBuilder = ref(false);
const searchQuery = ref('');
const filterStatus = ref('');
const viewMode = ref('list'); // 'list' or 'folders'
const selectedFolder = ref('');
const folders = ref([]);

const filteredTemplates = computed(() => {
  let list = templates.value;
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase();
    list = list.filter(t => t.name.toLowerCase().includes(q));
  }
  if (filterStatus.value) {
    list = list.filter(t => t.status === filterStatus.value);
  }
  if (selectedFolder.value) {
    list = list.filter(t => t.folder === selectedFolder.value);
  }
  return list;
});

const groupedByFolder = computed(() => {
  const groups = {};
  const list = filteredTemplates.value;

  list.forEach(t => {
    const folder = t.folder || 'Sem Pasta';
    if (!groups[folder]) groups[folder] = [];
    groups[folder].push(t);
  });

  // Sort folder names, keeping "Sem Pasta" at the end
  const sorted = Object.keys(groups).sort((a, b) => {
    if (a === 'Sem Pasta') return 1;
    if (b === 'Sem Pasta') return -1;
    return a.localeCompare(b, 'pt-BR');
  });

  return sorted.map(name => ({ name, templates: groups[name] }));
});

const statusConfig = {
  approved: { label: 'Aprovado', cls: 'bg-emerald-100 text-emerald-700' },
  pending: { label: 'Pendente', cls: 'bg-amber-100 text-amber-700' },
  rejected: { label: 'Rejeitado', cls: 'bg-red-100 text-red-700' },
  paused: { label: 'Pausado', cls: 'bg-slate-100 text-slate-600' },
  disabled: { label: 'Desativado', cls: 'bg-red-100 text-red-600' },
  in_appeal: { label: 'Em Recurso', cls: 'bg-orange-100 text-orange-700' },
  draft: { label: 'Rascunho', cls: 'bg-slate-100 text-slate-500' },
};

const categoryLabel = {
  marketing: 'Marketing',
  utility: 'Utilitario',
  authentication: 'Autenticacao',
};

const fetchTemplates = async () => {
  if (!selectedInboxId.value) return;
  loading.value = true;
  try {
    const res = await WhatsappCampaignsAPI.getTemplates(selectedInboxId.value);
    templates.value = res.data || [];
    buildFolderList();
  } catch {
    useAlert('Erro ao carregar templates');
  } finally {
    loading.value = false;
  }
};

const buildFolderList = () => {
  const folderMap = {};
  templates.value.forEach(t => {
    if (t.folder) {
      folderMap[t.folder] = (folderMap[t.folder] || 0) + 1;
    }
  });
  folders.value = Object.entries(folderMap)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
};

const syncTemplates = async () => {
  if (!selectedInboxId.value) return;
  syncing.value = true;
  try {
    await WhatsappCampaignsAPI.syncTemplates(selectedInboxId.value);
    useAlert('Sincronizacao agendada. Aguarde alguns instantes.');
    setTimeout(fetchTemplates, 3000);
  } catch {
    useAlert('Erro ao sincronizar templates');
  } finally {
    syncing.value = false;
  }
};

const deleteTemplate = async id => {
  if (!confirm('Excluir este template tambem o remove da Meta. Confirmar?')) return;
  deletingId.value = id;
  try {
    await WhatsappCampaignsAPI.deleteTemplate(selectedInboxId.value, id);
    templates.value = templates.value.filter(t => t.id !== id);
    buildFolderList();
    useAlert('Template excluido');
  } catch (e) {
    useAlert(e?.response?.data?.error || 'Erro ao excluir template');
  } finally {
    deletingId.value = null;
  }
};

const onTemplateCreated = template => {
  templates.value.unshift(template);
  buildFolderList();
  showBuilder.value = false;
  useAlert('Template enviado para aprovacao da Meta');
};

const selectFolder = folderName => {
  if (selectedFolder.value === folderName) {
    selectedFolder.value = '';
  } else {
    selectedFolder.value = folderName;
  }
};

const getBodyPreview = tpl => {
  const body = (tpl.components || []).find(c => c.type === 'BODY');
  if (!body?.text) return '';
  const text = body.text;
  return text.length > 100 ? text.substring(0, 100) + '...' : text;
};

const folderTemplates = computed(() => {
  if (!selectedFolder.value) return [];
  if (selectedFolder.value === '__none__') {
    return filteredTemplates.value.filter(t => !t.folder);
  }
  return filteredTemplates.value.filter(t => t.folder === selectedFolder.value);
});

const expandedFolders = ref({});
const toggleFolder = folderName => {
  expandedFolders.value[folderName] = !expandedFolders.value[folderName];
};
const isFolderExpanded = folderName => expandedFolders.value[folderName] !== false;

onMounted(() => {
  if (whatsappInboxes.value?.length === 1) {
    selectedInboxId.value = whatsappInboxes.value[0].id;
    fetchTemplates();
  }
});

watch(selectedInboxId, () => {
  selectedFolder.value = '';
});
</script>

<template>
  <CampaignLayout
    header-title="Gerenciar Templates"
    button-label="Criar Template"
    @click="showBuilder = true"
  >
    <div class="space-y-5 py-4">
      <!-- Nav tabs -->
      <div class="flex gap-1 border-b border-n-alpha-3 pb-1">
        <router-link
          :to="{ name: 'campaigns_whatsapp_index' }"
          class="rounded-lg px-4 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
          active-class="bg-n-alpha-2 text-n-slate-12"
        >
          Campanhas
        </router-link>
        <router-link
          :to="{ name: 'campaigns_whatsapp_templates' }"
          class="rounded-lg px-4 py-2 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
          active-class="bg-n-alpha-2 text-n-slate-12"
        >
          Templates
        </router-link>
      </div>

      <!-- Filters -->
      <div class="rounded-2xl border border-n-slate-4 bg-gradient-to-br from-n-slate-1 via-n-alpha-1 to-n-slate-2 p-4 shadow-sm">
        <div class="grid grid-cols-1 gap-3 md:grid-cols-5">
          <!-- Inbox selector -->
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">Caixa de Entrada</label>
            <select
              v-model="selectedInboxId"
              class="h-10 rounded-xl border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none transition focus:border-n-blue-8"
              @change="fetchTemplates"
            >
              <option value="">Selecione uma caixa...</option>
              <option v-for="inbox in whatsappInboxes" :key="inbox.id" :value="inbox.id">
                {{ inbox.name }}
              </option>
            </select>
          </div>

          <!-- Search -->
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">Buscar</label>
            <input
              v-model="searchQuery"
              placeholder="Nome do template..."
              class="h-10 rounded-xl border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none transition focus:border-n-blue-8"
            />
          </div>

          <!-- Status filter -->
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">Status</label>
            <select
              v-model="filterStatus"
              class="h-10 rounded-xl border border-n-slate-5 bg-n-slate-1 px-3 text-sm outline-none transition focus:border-n-blue-8"
            >
              <option value="">Todos</option>
              <option value="approved">Aprovado</option>
              <option value="pending">Pendente</option>
              <option value="rejected">Rejeitado</option>
              <option value="paused">Pausado</option>
              <option value="draft">Rascunho</option>
            </select>
          </div>

          <!-- View mode toggle -->
          <div class="flex flex-col gap-1">
            <label class="text-sm font-medium text-n-slate-12">Visualizacao</label>
            <div class="flex h-10 items-center gap-1 rounded-xl border border-n-slate-5 bg-n-slate-1 p-1">
              <button
                class="flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium transition"
                :class="viewMode === 'list' ? 'bg-n-brand text-white shadow-sm' : 'text-n-slate-10 hover:bg-n-alpha-2'"
                @click="viewMode = 'list'; selectedFolder = ''"
              >
                <span class="i-lucide-list text-sm" />
                Lista
              </button>
              <button
                class="flex flex-1 items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs font-medium transition"
                :class="viewMode === 'folders' ? 'bg-n-brand text-white shadow-sm' : 'text-n-slate-10 hover:bg-n-alpha-2'"
                @click="viewMode = 'folders'; selectedFolder = ''"
              >
                <span class="i-lucide-folder text-sm" />
                Pastas
              </button>
            </div>
          </div>

          <!-- Sync button -->
          <div class="flex flex-col justify-end">
            <Button
              :label="syncing ? 'Sincronizando...' : 'Sincronizar da Meta'"
              icon="i-lucide-refresh-cw"
              variant="secondary"
              size="sm"
              :disabled="!selectedInboxId || syncing"
              @click="syncTemplates"
            />
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="flex items-center justify-center py-16">
        <Spinner />
      </div>

      <!-- Empty: no inbox selected -->
      <div
        v-else-if="!selectedInboxId"
        class="flex flex-col items-center justify-center rounded-2xl border border-dashed border-n-slate-4 py-16 text-center"
      >
        <span class="i-lucide-inbox mb-3 text-4xl text-n-slate-8" />
        <p class="text-sm text-n-slate-10">Selecione uma caixa de entrada para ver os templates</p>
      </div>

      <!-- Empty: no templates -->
      <div
        v-else-if="filteredTemplates.length === 0"
        class="flex flex-col items-center justify-center rounded-2xl border border-dashed border-n-slate-4 py-16 text-center"
      >
        <span class="i-lucide-file-text mb-3 text-4xl text-n-slate-8" />
        <p class="text-sm font-medium text-n-slate-11">Nenhum template encontrado</p>
        <p class="mt-1 text-xs text-n-slate-9">Crie um novo template ou sincronize com a Meta</p>
        <Button
          label="Criar Template"
          icon="i-lucide-plus"
          size="sm"
          class="mt-4"
          @click="showBuilder = true"
        />
      </div>

      <!-- LIST VIEW -->
      <div v-else-if="viewMode === 'list'" class="overflow-hidden rounded-2xl border border-n-slate-4 bg-n-slate-1 shadow-sm">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-n-alpha-3 bg-n-surface-2">
              <th class="px-4 py-3 text-left font-medium text-n-slate-11">Nome</th>
              <th class="px-4 py-3 text-left font-medium text-n-slate-11">Pasta</th>
              <th class="px-4 py-3 text-left font-medium text-n-slate-11">Idioma</th>
              <th class="px-4 py-3 text-left font-medium text-n-slate-11">Categoria</th>
              <th class="px-4 py-3 text-left font-medium text-n-slate-11">Status</th>
              <th class="px-4 py-3 text-left font-medium text-n-slate-11">Atualizado</th>
              <th class="px-4 py-3 text-right font-medium text-n-slate-11">Acoes</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="tpl in filteredTemplates"
              :key="tpl.id"
              class="border-b border-n-alpha-2 transition hover:bg-n-alpha-1 last:border-0"
            >
              <td class="px-4 py-3">
                <div class="font-mono text-xs font-medium text-n-slate-12">{{ tpl.name }}</div>
                <div class="mt-0.5 text-xs text-n-slate-9 line-clamp-1">{{ getBodyPreview(tpl) }}</div>
              </td>
              <td class="px-4 py-3">
                <span
                  v-if="tpl.folder"
                  class="inline-flex items-center gap-1 rounded-md bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-10"
                >
                  <span class="i-lucide-folder text-[10px]" />
                  {{ tpl.folder }}
                </span>
                <span v-else class="text-xs text-n-slate-8">-</span>
              </td>
              <td class="px-4 py-3 text-n-slate-10">{{ tpl.language }}</td>
              <td class="px-4 py-3 text-n-slate-10">
                {{ categoryLabel[tpl.category] || tpl.category }}
              </td>
              <td class="px-4 py-3">
                <span
                  class="rounded-full px-2.5 py-0.5 text-xs font-medium"
                  :class="(statusConfig[tpl.status] || statusConfig.draft).cls"
                >
                  {{ (statusConfig[tpl.status] || statusConfig.draft).label }}
                </span>
              </td>
              <td class="px-4 py-3 text-n-slate-9">
                {{ new Date(tpl.updated_at * 1000).toLocaleDateString('pt-BR') }}
              </td>
              <td class="px-4 py-3 text-right">
                <button
                  class="rounded-lg p-1.5 text-n-slate-9 transition hover:bg-red-50 hover:text-red-600"
                  :disabled="deletingId === tpl.id"
                  @click="deleteTemplate(tpl.id)"
                >
                  <span
                    :class="deletingId === tpl.id ? 'i-lucide-loader-circle animate-spin' : 'i-lucide-trash-2'"
                    class="text-base"
                  />
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- FOLDERS VIEW -->
      <div v-else class="space-y-3">
        <!-- Folder cards grid (when no folder is selected) -->
        <div v-if="!selectedFolder && folders.length > 0" class="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          <button
            v-for="folder in folders"
            :key="folder.name"
            class="group flex items-center gap-3 rounded-2xl border border-n-slate-4 bg-n-slate-1 p-4 text-left shadow-sm transition hover:border-n-blue-7 hover:shadow-md"
            @click="selectFolder(folder.name)"
          >
            <div class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-n-blue-3 text-n-blue-9 transition group-hover:bg-n-blue-4">
              <span class="i-lucide-folder text-lg" />
            </div>
            <div class="min-w-0 flex-1">
              <div class="truncate text-sm font-medium text-n-slate-12">{{ folder.name }}</div>
              <div class="text-xs text-n-slate-9">{{ folder.count }} templates</div>
            </div>
            <span class="i-lucide-chevron-right text-n-slate-8 transition group-hover:text-n-blue-9" />
          </button>

          <!-- "No folder" card -->
          <button
            v-if="templates.some(t => !t.folder)"
            class="group flex items-center gap-3 rounded-2xl border border-dashed border-n-slate-4 bg-n-slate-1 p-4 text-left shadow-sm transition hover:border-n-slate-6 hover:shadow-md"
            @click="selectFolder('__none__')"
          >
            <div class="flex size-10 shrink-0 items-center justify-center rounded-xl bg-n-slate-3 text-n-slate-8">
              <span class="i-lucide-file-text text-lg" />
            </div>
            <div class="min-w-0 flex-1">
              <div class="truncate text-sm font-medium text-n-slate-11">Sem Pasta</div>
              <div class="text-xs text-n-slate-9">{{ templates.filter(t => !t.folder).length }} templates</div>
            </div>
            <span class="i-lucide-chevron-right text-n-slate-8" />
          </button>
        </div>

        <!-- Folder breadcrumb + templates inside folder -->
        <div v-if="selectedFolder">
          <button
            class="mb-3 flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-n-blue-9 transition hover:bg-n-blue-2"
            @click="selectedFolder = ''"
          >
            <span class="i-lucide-arrow-left text-sm" />
            Voltar para pastas
          </button>
          <div class="mb-3 flex items-center gap-2">
            <span class="i-lucide-folder-open text-lg text-n-blue-9" />
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ selectedFolder === '__none__' ? 'Sem Pasta' : selectedFolder }}
            </h3>
            <span class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-10">
              {{ folderTemplates.length }} templates
            </span>
          </div>
        </div>

        <!-- Accordion folders view (when no specific folder selected, showing all grouped) -->
        <div v-if="!selectedFolder && folders.length === 0 && templates.length > 0">
          <p class="text-sm text-n-slate-10">Nenhuma pasta encontrada. Todos os templates estao sem pasta.</p>
        </div>

        <!-- Templates inside selected folder (table) -->
        <div
          v-if="selectedFolder && folderTemplates.length > 0"
          class="overflow-hidden rounded-2xl border border-n-slate-4 bg-n-slate-1 shadow-sm"
        >
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-n-alpha-3 bg-n-surface-2">
                <th class="px-4 py-3 text-left font-medium text-n-slate-11">Nome</th>
                <th class="px-4 py-3 text-left font-medium text-n-slate-11">Conteudo</th>
                <th class="px-4 py-3 text-left font-medium text-n-slate-11">Categoria</th>
                <th class="px-4 py-3 text-left font-medium text-n-slate-11">Status</th>
                <th class="px-4 py-3 text-right font-medium text-n-slate-11">Acoes</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="tpl in folderTemplates"
                :key="tpl.id"
                class="border-b border-n-alpha-2 transition hover:bg-n-alpha-1 last:border-0"
              >
                <td class="px-4 py-3 font-mono text-xs font-medium text-n-slate-12">
                  {{ tpl.name }}
                </td>
                <td class="max-w-xs px-4 py-3 text-xs text-n-slate-10 line-clamp-2">
                  {{ getBodyPreview(tpl) }}
                </td>
                <td class="px-4 py-3 text-n-slate-10">
                  {{ categoryLabel[tpl.category] || tpl.category }}
                </td>
                <td class="px-4 py-3">
                  <span
                    class="rounded-full px-2.5 py-0.5 text-xs font-medium"
                    :class="(statusConfig[tpl.status] || statusConfig.draft).cls"
                  >
                    {{ (statusConfig[tpl.status] || statusConfig.draft).label }}
                  </span>
                </td>
                <td class="px-4 py-3 text-right">
                  <button
                    class="rounded-lg p-1.5 text-n-slate-9 transition hover:bg-red-50 hover:text-red-600"
                    :disabled="deletingId === tpl.id"
                    @click="deleteTemplate(tpl.id)"
                  >
                    <span
                      :class="deletingId === tpl.id ? 'i-lucide-loader-circle animate-spin' : 'i-lucide-trash-2'"
                      class="text-base"
                    />
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Grouped accordion view (all folders expanded) -->
        <div v-if="!selectedFolder && groupedByFolder.length > 0" class="space-y-3">
          <div
            v-for="group in groupedByFolder"
            :key="group.name"
            class="overflow-hidden rounded-2xl border border-n-slate-4 bg-n-slate-1 shadow-sm"
          >
            <button
              class="flex w-full items-center gap-2 border-b border-n-alpha-2 bg-n-surface-2 px-4 py-3 text-left transition hover:bg-n-alpha-2"
              @click="toggleFolder(group.name)"
            >
              <span
                class="text-sm text-n-slate-9 transition-transform"
                :class="isFolderExpanded(group.name) ? 'i-lucide-chevron-down' : 'i-lucide-chevron-right'"
              />
              <span class="i-lucide-folder text-n-blue-9" />
              <span class="text-sm font-medium text-n-slate-12">{{ group.name }}</span>
              <span class="rounded-full bg-n-alpha-2 px-2 py-0.5 text-xs text-n-slate-10">{{ group.templates.length }}</span>
            </button>
            <div v-if="isFolderExpanded(group.name)">
              <table class="w-full text-sm">
                <tbody>
                  <tr
                    v-for="tpl in group.templates"
                    :key="tpl.id"
                    class="border-b border-n-alpha-2 transition hover:bg-n-alpha-1 last:border-0"
                  >
                    <td class="px-4 py-2.5 font-mono text-xs font-medium text-n-slate-12" style="width: 25%">
                      {{ tpl.name }}
                    </td>
                    <td class="max-w-xs px-4 py-2.5 text-xs text-n-slate-10 line-clamp-1" style="width: 35%">
                      {{ getBodyPreview(tpl) }}
                    </td>
                    <td class="px-4 py-2.5 text-n-slate-10" style="width: 12%">
                      {{ categoryLabel[tpl.category] || tpl.category }}
                    </td>
                    <td class="px-4 py-2.5" style="width: 12%">
                      <span
                        class="rounded-full px-2.5 py-0.5 text-xs font-medium"
                        :class="(statusConfig[tpl.status] || statusConfig.draft).cls"
                      >
                        {{ (statusConfig[tpl.status] || statusConfig.draft).label }}
                      </span>
                    </td>
                    <td class="px-4 py-2.5 text-right" style="width: 8%">
                      <button
                        class="rounded-lg p-1.5 text-n-slate-9 transition hover:bg-red-50 hover:text-red-600"
                        :disabled="deletingId === tpl.id"
                        @click="deleteTemplate(tpl.id)"
                      >
                        <span
                          :class="deletingId === tpl.id ? 'i-lucide-loader-circle animate-spin' : 'i-lucide-trash-2'"
                          class="text-base"
                        />
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </CampaignLayout>

  <!-- Builder modal -->
  <WhatsAppTemplateBuilderModal
    v-if="showBuilder"
    :inbox-id="selectedInboxId"
    :inboxes="whatsappInboxes"
    @close="showBuilder = false"
    @created="onTemplateCreated"
  />
</template>
